import { createFileRoute } from "@tanstack/react-router"
import { Typography } from "@/components/ui/typography"

export const Route = createFileRoute("/_public/terms-of-service")({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <main>
        <article>
          <header>
            <Typography as="h1" size="3xl" className="mb-8">
              Terms Of Service
            </Typography>
          </header>

          {/* Standard Users Section */}
          <section>
            <Typography as="h2" size="2xl" className="mt-8 mb-4">
              Standard Users
            </Typography>
            <Typography as="p" className="mb-6 italic">
              Effective Date: June 5, 2026
            </Typography>

            <Typography as="p" className="mb-4">
              Welcome to Legacy Keeper! This Terms of Service Agreement ("Agreement") is a legal
              agreement between Traction Technologies Inc. / 1546045 B.C. LTD. ("Company," "we,"
              "us," or "our") and you ("User," "you," or "your") regarding your use of the Legacy
              Keeper service ("Service").
            </Typography>

            <Typography as="p" className="mb-6 font-semibold">
              By accessing or using Legacy Keeper, you agree to be bound by these terms. If you do
              not agree with any part of these terms, do not use the Service.
            </Typography>
          </section>

          {/* Section 1: Understanding Legacy Keeper */}
          <section>
            <Typography as="h3" size="xl" className="mt-8 mb-4">
              1. Understanding Legacy Keeper
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              1.1 What Is Legacy Keeper?
            </Typography>
            <Typography as="p" className="mb-4">
              Legacy Keeper is an AI-powered estate planning platform designed to help you organize
              your legacy, prepare estate planning documents, and communicate your wishes to your
              family. Our Service uses advanced artificial intelligence technology to guide you
              through the estate planning process.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              1.2 Who Can Use Legacy Keeper?
            </Typography>
            <Typography as="p" className="mb-4">
              To use Legacy Keeper, you must be at least 18 years of age, have the legal capacity to
              enter into this Agreement, and create an account with accurate information.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              1.3 Important: We Are Not Replacing Professional Advice
            </Typography>
            <Typography as="p" className="mb-6">
              Legacy Keeper is a helpful tool, but it does not replace the advice of qualified
              professionals. We are not attorneys, financial advisors, accountants, or tax
              professionals, and the Service does not provide legal, financial, tax, or professional
              advice for individual users. For complex estate planning situations, we strongly
              recommend consulting with qualified professionals in your area.
            </Typography>
          </section>

          {/* Section 2: How the Service Works */}
          <section>
            <Typography as="h3" size="xl" className="mt-8 mb-4">
              2. How the Service Works
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              2.1 AI Technology
            </Typography>
            <Typography as="p" className="mb-4">
              Legacy Keeper uses artificial intelligence (AI) to help generate documents and provide
              information. While we work hard to make our AI accurate and helpful, AI-generated
              content may occasionally contain errors or may not perfectly fit your unique
              situation. You should always review all content carefully before relying on it.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              2.2 Your Responsibilities
            </Typography>
            <Typography as="p" className="mb-4">
              When using Legacy Keeper, you agree to provide accurate information, review all
              AI-generated content carefully, keep your account credentials secure, use the Service
              only for lawful purposes, not share your credentials, and consult professionals for
              legal validation of estate planning documents.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              2.3 What You Cannot Do
            </Typography>
            <Typography as="p" className="mb-6">
              You agree not to use the Service for illegal purposes; upload viruses, malware, or
              harmful code; attempt to hack, reverse engineer, or compromise the Service; create
              fraudulent documents or content; share false or misleading information; or interfere
              with other users' access to the Service.
            </Typography>
          </section>

          {/* Section 3: Sharing Your Information and Collaboration */}
          <section>
            <Typography as="h3" size="xl" className="mt-8 mb-4">
              3. Sharing Your Information and Collaboration
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              3.1 You Control Who Sees Your Data
            </Typography>
            <Typography as="p" className="mb-4">
              Your estate planning information belongs to you, and you have complete control over
              who can access it. You decide who to share your information with, and you can revoke
              access at any time.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              3.2 Working with Professional Advisors
            </Typography>
            <Typography as="p" className="mb-4">
              You may work with professional advisors (such as attorneys, financial advisors, or
              accountants) who also use Legacy Keeper:
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>
                <strong>Receiving an Invitation:</strong> Your professional advisor will send you an
                invitation to collaborate. If they create an account on your behalf, they can begin
                adding and accessing information for you.
              </li>
              <li>
                <strong>Reviewing and Accepting:</strong> If you already have an account, you must
                explicitly accept the invitation before your advisor can access your data. By
                accepting, you authorize them to view, input, and edit your estate planning
                information.
              </li>
              <li>
                <strong>Automatic Account Creation:</strong> If you do not already have an account
                associated with your email address, a Professional User may create and access an
                account on your behalf.
              </li>
              <li>
                <strong>Revoking Access:</strong> You can revoke your advisor's access at any time
                through your collaboration pages.
              </li>
            </ul>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              3.3 What Professional Advisors Can Do
            </Typography>
            <Typography as="p" className="mb-4">
              When you accept a professional advisor's invitation, they gain the ability to view all
              of your estate planning data and documents, input new information on your behalf, and
              edit existing information. You remain responsible for reviewing and approving all
              changes made by your professional advisors.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              3.4 Data Used Off the Legacy Keeper System
            </Typography>
            <Typography as="p" className="mb-6">
              Any time you share your data with a Professional User, they may export, download, or
              copy that data and use it outside of Legacy Keeper. Once your data is taken off the
              Legacy Keeper system, the Professional User is independently responsible for storing,
              securing, and handling that data in accordance with their own legal, regulatory,
              fiduciary, and professional obligations. Legacy Keeper has no control over, and is not
              responsible for, copies of your data held outside of our platform. You should only
              share data with professionals you trust, and you may wish to confirm how they will
              store and protect it.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              3.5 Partner Service Connections (API Integrations)
            </Typography>
            <Typography as="p" className="mb-4">
              Legacy Keeper offers an API and MCP integration that can connect the Service to
              approved partner tools, such as dashboards, financial planning tools, or CRM systems.
              Legacy Keeper does not offer an open or public API; every integration is individually
              vetted and governed by a partnership agreement.
            </Typography>
            <Typography as="p" className="mb-4 font-semibold">
              Connections you make yourself.
            </Typography>
            <Typography as="p" className="mb-4">
              If you choose to connect your account directly to an approved partner service, you
              take full responsibility for that connection and for how the partner service stores,
              secures, and uses the data you share with it. Legacy Keeper is not responsible for how
              a partner service you have connected handles your data. You can revoke a connection
              you have made at any time, directly from your dashboard.
            </Typography>
            <Typography as="p" className="mb-4 font-semibold">
              Connections made by your advisor.
            </Typography>
            <Typography as="p" className="mb-6">
              Your professional advisor may connect approved partner services they use to
              collaborate with you. When your advisor connects a partner service that can access
              your data, that service falls under your advisor's responsibility and acts as an
              extension of their access. We will notify you of the new connection, and you can
              request that your advisor revoke the partner service's access. You also retain the
              right to revoke your advisor's access to your account at any time, which ends any
              partner access derived from it.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              3.6 Sharing with Family Members and Other Advisors
            </Typography>
            <Typography as="p" className="mb-4">
              You can invite other Standard Users (non-professionals) to <strong>view</strong>{" "}
              specific information you choose to share — they cannot edit or add to it. If you want
              someone to help you input and edit information, they must be registered as a
              Professional User.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              3.7 Your Responsibility When Sharing
            </Typography>
            <Typography as="p" className="mb-6">
              You take full responsibility for who you share your information with. You are solely
              responsible for deciding who to invite and what to share, you should only share with
              people you trust, and you are responsible for managing permissions and revoking
              access. We are not liable for unauthorized access that results from you sharing your
              information with someone.
            </Typography>
          </section>

          {/* Section 4: Your Privacy and Data Security */}
          <section>
            <Typography as="h3" size="xl" className="mt-8 mb-4">
              4. Your Privacy and Data Security
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              4.1 We Take Your Privacy Seriously
            </Typography>
            <Typography as="p" className="mb-4">
              Your personal information and estate planning data are extremely important to us. We
              use enterprise-grade security measures to protect your information.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              4.2 How We Protect Your Data
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>
                <strong>Encryption in transit:</strong> All data is encrypted using TLS (version 1.2
                or higher).
              </li>
              <li>
                <strong>Encryption at rest:</strong> All stored data is encrypted using AES-256
                encryption.
              </li>
              <li>
                <strong>Secure infrastructure:</strong> Your data is hosted on Amazon Web Services
                (AWS) with enterprise-grade security controls and network isolation.
              </li>
              <li>
                <strong>Access controls:</strong> Secure authentication systems protect your
                account, and access to identifiable data is strictly limited.
              </li>
              <li>
                <strong>Backups:</strong> We maintain automated, encrypted backups on a rolling
                30-day cycle to protect against data loss.
              </li>
            </ul>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              4.3 How AI Works With Your Data
            </Typography>
            <Typography as="p" className="mb-4">
              Legacy Keeper's AI features are powered by Amazon Bedrock.
            </Typography>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>Foundation models:</strong> The data you provide is never used to train the
                underlying foundation models that power our AI. Your information is not used to
                train these models, is not retained by our AI provider, and is not used to improve
                AI services for any other party. Each interaction is processed in real time within
                our secure AWS environment.
              </li>
              <li>
                <strong>Our own service improvement:</strong> To improve our features, we may use
                anonymized, de-identified content for context building and internal model
                development. Information is anonymized so that it can no longer be associated with
                you, and we apply safeguards to prevent any individual's information from being
                reproduced. We do not use identifiable personal information to train or fine-tune
                our models.
              </li>
            </ul>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              4.4 We Do Not Sell or Monetize Your Data
            </Typography>
            <Typography as="p" className="mb-4">
              We never sell, rent, trade, or otherwise monetize your personal information. We only
              share your data when (a) you explicitly authorize it; (b) it is necessary to provide
              the Service (such as with our secure hosting and AI providers, who are contractually
              bound to keep your data confidential); or (c) we are required by law.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              4.5 Your Privacy Rights
            </Typography>
            <Typography as="p" className="mb-4">
              You have the right to access your personal data, request corrections, request
              deletion, and export your data in a portable format. For more details, please review
              our Privacy Policy, which is incorporated into this Agreement.
            </Typography>
          </section>

          {/* Section 5: Your Account */}
          <section>
            <Typography as="h3" size="xl" className="mt-8 mb-4">
              5. Your Account
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              5.1 Creating Your Account
            </Typography>
            <Typography as="p" className="mb-4">
              To use Legacy Keeper, you'll need to create an account with accurate information and
              keep it updated. You are responsible for all activities that occur under your account.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              5.2 Account Security
            </Typography>
            <Typography as="p" className="mb-6">
              Choose a strong, unique password and keep it confidential. Never share your
              credentials. If you suspect unauthorized access, notify us immediately at{" "}
              <a href="mailto:general@legacykeeper.app" className="text-primary hover:underline">
                general@legacykeeper.app
              </a>
              . We recommend enabling multi-factor authentication.
            </Typography>
          </section>

          {/* Section 6: Subscription and Payment */}
          <section>
            <Typography as="h3" size="xl" className="mt-8 mb-4">
              6. Subscription and Payment
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              6.1 Free and Paid Features
            </Typography>
            <Typography as="p" className="mb-4">
              Legacy Keeper offers both free features and premium features that require a paid
              subscription, as described on our website.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              6.2 Subscription Terms
            </Typography>
            <Typography as="p" className="mb-4">
              If you subscribe, you agree to pay the subscription fees in effect when you subscribe.
              Subscriptions renew automatically unless cancelled before the renewal date. We'll
              notify you of any price changes at least 30 days in advance.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              6.3 Cancellation
            </Typography>
            <Typography as="p" className="mb-4">
              You can cancel at any time through your account settings. Cancellation takes effect at
              the end of your current billing period. We do not provide refunds for partial
              subscription periods, except as required by law.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              6.4 Payment Methods
            </Typography>
            <Typography as="p" className="mb-6">
              You authorize us to charge your chosen payment method for all fees. If your payment
              fails, we may suspend access to premium features until payment is received. You are
              responsible for any applicable taxes.
            </Typography>
          </section>

          {/* Section 7: Ownership and Your Content */}
          <section>
            <Typography as="h3" size="xl" className="mt-8 mb-4">
              7. Ownership and Your Content
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              7.1 We Own the Service
            </Typography>
            <Typography as="p" className="mb-4">
              Legacy Keeper, including all software, AI technology, features, and content we
              provide, is owned by 1546045 B.C. LTD. and protected by intellectual property laws.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              7.2 You Own Your Content
            </Typography>
            <Typography as="p" className="mb-4">
              All the information, documents, and content you create or upload belongs to you. By
              using the Service, you grant us permission to store, process, and display your content
              solely to provide the Service to you. This permission ends when you delete your
              content or close your account.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              7.3 AI-Generated Content
            </Typography>
            <Typography as="p" className="mb-6">
              Documents and suggestions generated by our AI based on your input are provided for
              your use. Because AI can generate similar content for different users with similar
              requests, we cannot guarantee that AI-generated content will be unique to you.
            </Typography>
          </section>

          {/* Section 8: Important Disclaimers */}
          <section>
            <Typography as="h3" size="xl" className="mt-8 mb-4">
              8. Important Disclaimers
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              8.1 No Professional Relationship
            </Typography>
            <Typography as="p" className="mb-4">
              Using Legacy Keeper does not create an attorney-client, financial advisor-client, or
              any other professional relationship between you and Traction Technologies Inc. /
              1546045 B.C. LTD. We are a technology company providing tools, not professional
              services.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              8.2 Not Legal Advice
            </Typography>
            <Typography as="p" className="mb-4">
              Legacy Keeper provides information and generates documents, but this is not legal,
              financial, or tax advice. We strongly encourage you to have any documents reviewed by
              a qualified attorney in your jurisdiction before relying on them.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              8.3 Service Provided "As Is"
            </Typography>
            <Typography as="p" className="mb-4">
              Legacy Keeper is provided "as is" and "as available." We cannot guarantee that the
              Service will always be available, error-free, or secure, or that AI-generated content
              will be accurate, complete, or suitable for your needs.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              8.4 AI Technology Limitations
            </Typography>
            <Typography as="p" className="mb-6">
              AI-generated content may contain inaccuracies, may not reflect the most current laws,
              or may not perfectly fit your situation. You should always carefully review and verify
              AI-generated content and consult with professionals when needed.
            </Typography>
          </section>

          {/* Section 9: Limitation of Liability */}
          <section>
            <Typography as="h3" size="xl" className="mt-8 mb-4">
              9. Limitation of Liability
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              9.1 What We're Not Responsible For
            </Typography>
            <Typography as="p" className="mb-4">
              To the maximum extent permitted by law, Traction Technologies Inc. / 1546045 B.C. LTD.
              and its officers, directors, employees, and partners are not liable for any indirect,
              incidental, special, or consequential damages arising from your use of the Service,
              including lost profits, lost data, or costs of substitute services.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              9.2 Maximum Liability
            </Typography>
            <Typography as="p" className="mb-6">
              If we are found liable to you for any reason, our total liability will not exceed the
              amount you paid us for the Service during the 12 months before the problem occurred.
              If you are using a free version of the Service, our liability is limited to $100.
            </Typography>
          </section>

          {/* Section 10: Your Data and Account Management */}
          <section>
            <Typography as="h3" size="xl" className="mt-8 mb-4">
              10. Your Data and Account Management
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              10.1 Data Retention
            </Typography>
            <Typography as="p" className="mb-4">
              We keep your data as long as your account is active and as needed to provide the
              Service. We also retain certain data as required by law (such as payment records for 7
              years).
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              10.2 Account Data Deletion
            </Typography>
            <Typography as="p" className="mb-4">
              You can delete all of your account data at any time directly from your account
              settings page. When you confirm deletion, your data is removed from our active systems
              promptly and is fully purged from all backup systems within 30 days. After this
              period, no copies of your deleted data remain on Legacy Keeper's systems. Please see
              our Data Deletion Agreement for full details.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              10.3 Exporting Your Data
            </Typography>
            <Typography as="p" className="mb-6">
              You can request an export of your data at any time through your account settings. We
              strongly recommend exporting any information you wish to retain before deleting your
              data.
            </Typography>
          </section>

          {/* Section 11: Termination */}
          <section>
            <Typography as="h3" size="xl" className="mt-8 mb-4">
              11. Termination
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              11.1 Closing Your Account
            </Typography>
            <Typography as="p" className="mb-4">
              You can close your account at any time. If you have a paid subscription, your
              subscription will be cancelled and you won't be charged again.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              11.2 When We May Suspend or Close Your Account
            </Typography>
            <Typography as="p" className="mb-4">
              We may suspend or terminate your account if you violate this Agreement, your account
              poses a security risk, we are required to by law, you don't pay required fees, or we
              discontinue the Service. We'll try to give reasonable notice, but may suspend access
              immediately if necessary to protect the Service or other users.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              11.3 What Happens After Termination
            </Typography>
            <Typography as="p" className="mb-6">
              When your account is closed, you'll lose access to the Service and your data will be
              deleted in accordance with our Data Deletion Agreement. Export any information you
              want to keep before closing your account. Certain provisions (such as limitations of
              liability and dispute resolution) survive termination.
            </Typography>
          </section>

          {/* Section 12: Changes to the Service and Terms */}
          <section>
            <Typography as="h3" size="xl" className="mt-8 mb-4">
              12. Changes to the Service and Terms
            </Typography>

            <Typography as="p" className="mb-6">
              We may add, change, or remove features from time to time. When we make material
              changes to these terms, we'll notify you by email and post the updated terms. Changes
              take effect 30 days after we notify you. If you continue using the Service after that
              date, you agree to the new terms.
            </Typography>
          </section>

          {/* Section 13: Resolving Disputes */}
          <section>
            <Typography as="h3" size="xl" className="mt-8 mb-4">
              13. Resolving Disputes
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              13.1 Let's Talk First
            </Typography>
            <Typography as="p" className="mb-4">
              If you have a problem with the Service, please contact us first at{" "}
              <a href="mailto:general@legacykeeper.app" className="text-primary hover:underline">
                general@legacykeeper.app
              </a>
              .
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              13.2 Arbitration
            </Typography>
            <Typography as="p" className="mb-4">
              If we can't resolve a dispute informally, you agree it will be resolved through
              binding arbitration rather than in court. Arbitration will be conducted under the
              Consumer Arbitration Rules of the American Arbitration Association (for U.S. users) or
              under applicable Canadian arbitration rules (for Canadian users), in Portland, Oregon
              (for U.S. users) or Vancouver, British Columbia (for Canadian users), or remotely if
              mutually agreed. Each party pays their own costs unless the arbitrator decides
              otherwise.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              13.3 No Class Actions
            </Typography>
            <Typography as="p" className="mb-4">
              Disputes will be resolved on an individual basis only. If this provision is found
              unenforceable, the entire arbitration section will not apply.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              13.4 Small Claims Court
            </Typography>
            <Typography as="p" className="mb-6">
              Either party may bring a qualifying claim in small claims court instead of
              arbitration.
            </Typography>
          </section>

          {/* Section 14: General Legal Terms */}
          <section>
            <Typography as="h3" size="xl" className="mt-8 mb-4">
              14. General Legal Terms
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              14.1 Which Laws Apply
            </Typography>
            <Typography as="p" className="mb-4">
              This Agreement is governed by the laws of British Columbia, Canada (if you access the
              Service through ca.legacykeeper.app) or Oregon, United States (if you access the
              Service through us.legacykeeper.app), without regard to conflict of law principles.
              The arbitration provisions are governed by the Federal Arbitration Act (for U.S.
              users) or applicable Canadian arbitration laws.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              14.2 Complete Agreement
            </Typography>
            <Typography as="p" className="mb-4">
              This Agreement, along with our Privacy Policy and Data Deletion Agreement, represents
              the complete agreement between you and Traction Technologies Inc. / 1546045 B.C. LTD.
              regarding the Service.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              14.3 If Part of This Agreement Is Unenforceable
            </Typography>
            <Typography as="p" className="mb-4">
              If a court finds any part unenforceable, the rest remains in effect, and the
              unenforceable part will be modified to be as close as possible to the original intent
              while being enforceable.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              14.4 We Can't Transfer This Agreement
            </Typography>
            <Typography as="p" className="mb-4">
              You cannot transfer your rights or obligations without our written permission. We may
              transfer our rights and obligations, including in connection with a merger or sale.
            </Typography>

            <Typography as="h4" size="lg" className="mt-6 mb-3">
              14.5 Notices
            </Typography>
            <Typography as="p" className="mb-6">
              We'll send important notices to the email address on your account. Contact us at{" "}
              <a href="mailto:general@legacykeeper.app" className="text-primary hover:underline">
                general@legacykeeper.app
              </a>{" "}
              for notices to us. Please keep your email address current.
            </Typography>
          </section>

          {/* Divider */}
          <hr className="my-12 border-gray-300" />

          {/* Professional Users Section */}
          <section>
            <Typography as="h2" size="2xl" className="mt-8 mb-4">
              Professional Users
            </Typography>
            <Typography as="p" className="mb-6 italic">
              Effective Date: June 5, 2026
            </Typography>

            {/* Professional Section 1: Understanding Your Role */}
            <section>
              <Typography as="h3" size="xl" className="mt-8 mb-4">
                1. Understanding Your Role as a Professional User
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                1.1 What Is a Professional User?
              </Typography>
              <Typography as="p" className="mb-4">
                Professional Users are licensed or certified professionals — including financial
                advisors, attorneys, accountants, and estate planners — who use Legacy Keeper to
                collaborate with their clients (Standard Users) on estate planning matters. As a
                Professional User, you have enhanced capabilities to input, edit, and manage client
                data.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                1.2 Professional Requirements
              </Typography>
              <Typography as="p" className="mb-4">
                To register as a Professional User, you must hold a valid professional license or
                certification relevant to estate planning, financial advisory, legal services, or
                accounting; have a fiduciary duty to your clients; be in good standing with your
                professional regulatory body; maintain appropriate professional liability insurance;
                and provide accurate professional credentials.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                1.3 Your Professional Responsibilities
              </Typography>
              <Typography as="p" className="mb-6">
                As a Professional User, you remain bound by all applicable professional standards,
                ethics rules, and legal obligations governing your profession. Legacy Keeper is a
                tool to assist you — it does not replace your professional judgment, expertise, or
                legal obligations. You are solely responsible for the advice you provide and the
                services you render to your clients.
              </Typography>
            </section>

            {/* Professional Section 2: How You Use the Service */}
            <section>
              <Typography as="h3" size="xl" className="mt-8 mb-4">
                2. How You Use the Service
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                2.1 Your Account
              </Typography>
              <Typography as="p" className="mb-4">
                You must create an account and provide accurate professional credentials. You are
                responsible for maintaining the confidentiality of your credentials and for all
                activities under your account.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                2.2 Your Responsibilities
              </Typography>
              <Typography as="p" className="mb-4">
                You agree to provide accurate and current professional credentials, maintain the
                security of your account, use the Service only for legitimate professional purposes,
                comply with all applicable laws and professional regulations, respect client
                confidentiality and privacy, and not misrepresent your credentials.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                2.3 Prohibited Activities
              </Typography>
              <Typography as="p" className="mb-6">
                You agree not to use the Service for any illegal or unauthorized purpose, access
                client accounts without proper authorization, share your credentials, attempt to
                circumvent security features, upload harmful code, or interfere with other users'
                access.
              </Typography>
            </section>

            {/* Professional Section 3: Working with Clients */}
            <section>
              <Typography as="h3" size="xl" className="mt-8 mb-4">
                3. Working with Clients
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                3.1 Client Collaboration Process
              </Typography>
              <Typography as="p" className="mb-4">
                You collaborate with clients (Standard Users) by sending an invitation through the
                Service, optionally creating an account on behalf of a client to set up their
                profile, and accessing their data once they accept (or once an account you created
                on their behalf is active). The client can revoke your access at any time.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                3.2 Your Access to Client Data
              </Typography>
              <Typography as="p" className="mb-4">
                When a client accepts your invitation, you can view, input, and edit the client's
                estate planning data. The client remains the owner of their data and retains
                ultimate control, and is responsible for reviewing and approving changes you make.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                3.3 Client Confidentiality
              </Typography>
              <Typography as="p" className="mb-4">
                You must maintain the confidentiality of all client information accessed through
                Legacy Keeper in accordance with applicable professional standards and laws. You are
                solely responsible for complying with all confidentiality and privacy obligations
                governing your profession.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                3.4 Data Used Off the Legacy Keeper System
              </Typography>
              <Typography as="p" className="mb-4">
                When you export, download, or copy client data and use it outside of Legacy Keeper,{" "}
                <strong>
                  you are independently responsible for storing, securing, and handling that data
                </strong>{" "}
                in accordance with your own legal, regulatory, fiduciary, and professional
                obligations. These may include the Gramm-Leach-Bliley Act and the FTC Safeguards
                Rule, SEC Regulation S-P, applicable Canadian privacy law, and your professional
                conduct standards. Legacy Keeper's security controls apply only to data within our
                platform; once data leaves it, securing that data is your responsibility, not ours.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                3.5 When Client Access Ends
              </Typography>
              <Typography as="p" className="mb-4">
                When a client revokes your access or deletes their account, you immediately lose
                access to their data through Legacy Keeper. You remain responsible for any client
                information you previously downloaded or copied, and your professional obligations
                regarding client data retention continue to apply.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                3.6 Connecting Partner Services via API or MCP
              </Typography>
              <Typography as="p" className="mb-4">
                Legacy Keeper offers an API and MCP integration that allows you to connect approved
                partner services — such as your own dashboards, financial planning tools, or CRM
                systems — to the platform. The following terms apply:
              </Typography>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>
                  <strong>Vetted partnerships only.</strong> We do not offer an open or public API.
                  You may only connect partner services that Legacy Keeper has vetted and approved
                  under a partnership agreement, and you may not attempt to access the API or
                  connect any service that has not been authorized.
                </li>
                <li>
                  <strong>Connected services fall under you.</strong> A connected partner service
                  acts as an extension of your access. Its access to client data is derived from,
                  and limited to, your own access. You are fully responsible for the connected
                  service's storage, security, and use of client data, in the same way you are
                  responsible for client data you take off the Legacy Keeper platform under Section
                  3.4. This includes ensuring the connected service complies with your legal,
                  regulatory, fiduciary, and professional obligations.
                </li>
                <li>
                  <strong>Client notification and revocation.</strong> We will notify affected
                  clients when you connect a partner service that can access their data. A client
                  may request that you revoke a partner service's access, and you must honor
                  reasonable revocation requests consistent with your professional obligations. You
                  must promptly disconnect a partner service when a client requests it, when it is
                  no longer needed, or when the underlying partnership ends.
                </li>
                <li>
                  <strong>Authorization.</strong> You represent that you have the authority to
                  connect each partner service and to share client data with it for legitimate
                  professional purposes.
                </li>
              </ul>
            </section>

            {/* Professional Section 4: Professional Standards and Liability */}
            <section>
              <Typography as="h3" size="xl" className="mt-8 mb-4">
                4. Professional Standards and Liability
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                4.1 No Substitute for Professional Judgment
              </Typography>
              <Typography as="p" className="mb-4">
                Legacy Keeper does not replace your professional judgment, expertise, or
                obligations. You are solely responsible for the accuracy and appropriateness of
                advice you provide, compliance with all applicable professional standards,
                maintaining liability insurance, and verifying all AI-generated content before
                relying on it or sharing it with clients.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                4.2 AI Technology Limitations
              </Typography>
              <Typography as="p" className="mb-4">
                AI-generated content may contain errors, cannot replace professional expertise, and
                must be reviewed and verified by you before use. You are responsible for ensuring
                AI-generated documents are appropriate for your clients.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                4.3 Your Liability to Clients
              </Typography>
              <Typography as="p" className="mb-6">
                You acknowledge that Traction Technologies Inc. / 1546045 B.C. LTD. is not
                responsible for the professional services you provide, that you are solely liable
                for the advice, services, and documents you provide to clients, and that you will
                indemnify and hold us harmless from any claims arising from your professional
                services.
              </Typography>
            </section>

            {/* Professional Section 5: Data and Privacy */}
            <section>
              <Typography as="h3" size="xl" className="mt-8 mb-4">
                5. Data and Privacy
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                5.1 Client Data Ownership
              </Typography>
              <Typography as="p" className="mb-4">
                All client data belongs to the client. Clients grant you access to their data for
                professional advisory purposes only. You do not own client data accessed through
                Legacy Keeper.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                5.2 Data Security
              </Typography>
              <Typography as="p" className="mb-4">
                We use enterprise-grade security to protect all data on the platform, including TLS
                encryption in transit, AES-256 encryption at rest, and AWS-hosted infrastructure.
                You are responsible for maintaining the security of your credentials, using secure
                devices and networks, complying with applicable data protection laws, and
                implementing appropriate security measures for any client data you download.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                5.3 How We Use Data and AI
              </Typography>
              <Typography as="p" className="mb-6">
                Legacy Keeper's AI features are powered by Amazon Bedrock. Client data is never used
                to train the underlying foundation models, is not retained by our AI provider, and
                is not used to improve AI services for any other party. To improve our own features,
                we may use anonymized, de-identified content for context building and internal model
                development; this data is anonymized so that it can no longer be associated with any
                client, and we apply safeguards to prevent any individual's information from being
                reproduced. We do not use identifiable client data to train or fine-tune our models,
                and we never sell or monetize client data. Our Privacy Policy provides full details.
              </Typography>
            </section>

            {/* Professional Section 6: Subscription, Termination, and General Terms */}
            <section>
              <Typography as="h3" size="xl" className="mt-8 mb-4">
                6. Subscription, Termination, and General Terms
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                6.1 Subscription
              </Typography>
              <Typography as="p" className="mb-4">
                Professional subscriptions are billed per seat as described on our website, renew
                automatically unless cancelled, and are subject to the payment terms in the Standard
                User section above.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                6.2 Termination
              </Typography>
              <Typography as="p" className="mb-6">
                You may close your account at any time. If you delete your account, your access to
                all client accounts ends immediately, but your clients' data remains intact in their
                own accounts. You should notify your clients before deleting your account. Your data
                is deleted in accordance with our Data Deletion Agreement.
              </Typography>
            </section>

            {/* Professional Section 7: Resolving Disputes */}
            <section>
              <Typography as="h3" size="xl" className="mt-8 mb-4">
                7. Resolving Disputes
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                7.1 Let's Talk First
              </Typography>
              <Typography as="p" className="mb-4">
                If you have a problem with the Service, please contact us first at{" "}
                <a href="mailto:general@legacykeeper.app" className="text-primary hover:underline">
                  general@legacykeeper.app
                </a>
                .
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                7.2 Arbitration
              </Typography>
              <Typography as="p" className="mb-4">
                If we can't resolve a dispute informally, you agree it will be resolved through
                binding arbitration. Arbitration will be conducted under the Commercial Arbitration
                Rules of the American Arbitration Association (for U.S. users) or under applicable
                Canadian commercial arbitration rules (for Canadian users), in Portland, Oregon (for
                U.S. users) or Vancouver, British Columbia (for Canadian users), or remotely if
                mutually agreed. Each party pays their own costs unless the arbitrator decides
                otherwise.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                7.3 No Class Actions
              </Typography>
              <Typography as="p" className="mb-4">
                Disputes will be resolved on an individual basis only. If this provision is found
                unenforceable, the entire arbitration section will not apply.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                7.4 Small Claims Court
              </Typography>
              <Typography as="p" className="mb-6">
                Either party may bring a qualifying claim in small claims court instead of
                arbitration.
              </Typography>
            </section>

            {/* Professional Section 8: General Legal Terms */}
            <section>
              <Typography as="h3" size="xl" className="mt-8 mb-4">
                8. General Legal Terms
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                8.1 Which Laws Apply
              </Typography>
              <Typography as="p" className="mb-4">
                This Agreement is governed by the laws of British Columbia, Canada (if you access
                the Service through ca.legacykeeper.app) or Oregon, United States (if you access the
                Service through us.legacykeeper.app), without regard to conflict of law principles.
              </Typography>

              <Typography as="h4" size="lg" className="mt-6 mb-3">
                8.2 Complete Agreement
              </Typography>
              <Typography as="p" className="mb-6">
                This Agreement, along with our Privacy Policy and Data Deletion Agreement,
                represents the complete agreement between you and Traction Technologies Inc. /
                1546045 B.C. LTD. regarding the Service.
              </Typography>
            </section>
          </section>
        </article>
      </main>
    </div>
  )
}
